// Vyby Live REST client (/vbapi/live/v1) and media URL helpers. Live
// updates arrive over the core socket as `live:event`; REST is used for the
// initial load and as a fallback.
const { VUE_APP_AI_BACKEND, VUE_APP_S3 } = process.env

const BASE = `${VUE_APP_AI_BACKEND || ''}`.trim().replace(/\/+$/, '') + '/vbapi/live/v1'
const S3 = `${VUE_APP_S3 || ''}`.trim().replace(/\/+$/, '')

const request = async (jwt, method, path, body = null) => {
  if (!jwt) throw new Error('NOT_AUTHENTICATED')
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${jwt}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `HTTP_${res.status}`)
  }
  return data
}

export const liveApi = {
  streams: (jwt) => request(jwt, 'GET', '/streams'),
  rankings: (jwt) => request(jwt, 'GET', '/rankings'),
  stream: (jwt, streamId) => request(jwt, 'GET', `/streams/${encodeURIComponent(streamId)}`),
  sentences: (jwt, streamId, limit = 50) =>
    request(jwt, 'GET', `/streams/${encodeURIComponent(streamId)}/sentences?limit=${limit}`),
  moments: (jwt, streamId) => request(jwt, 'GET', `/streams/${encodeURIComponent(streamId)}/moments`),

  // metadata-only clips
  clips: (jwt, streamId = null) =>
    request(jwt, 'GET', `/clips${streamId ? `?streamId=${encodeURIComponent(streamId)}` : ''}`),
  createClip: (jwt, { streamId, startTime, endTime }) =>
    request(jwt, 'POST', '/clips', { streamId, startTime, endTime }),
  deleteClip: (jwt, clipId) => request(jwt, 'DELETE', `/clips/${encodeURIComponent(clipId)}`),
  extendClip: (jwt, clipId) => request(jwt, 'POST', `/clips/${encodeURIComponent(clipId)}/extend`),

  // clip outputs: background jobs, polled via exportJob
  downloadClip: (jwt, clipId) => request(jwt, 'POST', `/clips/${encodeURIComponent(clipId)}/download`),
  materializeClip: (jwt, clipId, projectId = null) =>
    request(jwt, 'POST', `/clips/${encodeURIComponent(clipId)}/materialize`, { projectId }),
  exportJob: (jwt, jobId) => request(jwt, 'GET', `/exports/${encodeURIComponent(jobId)}`),
  projects: (jwt) => request(jwt, 'GET', '/projects'),
}

// One-time browser download of a finished export job.
export const exportFileUrl = (jobId, jwt) =>
  `${BASE}/exports/${encodeURIComponent(jobId)}/file?jwt=${encodeURIComponent(jwt || '')}`

// VOD playlist of a clip's retained segments (works after the clip has left
// the live DVR window). hls.js cannot send headers for the first request
// without config, so the token rides in the query like the core socket.
export const clipPlaylistUrl = (clipId, jwt) =>
  `${BASE}/clips/${encodeURIComponent(clipId)}/playlist.m3u8?jwt=${encodeURIComponent(jwt || '')}`

// Relative live media path (e.g. "live/<id>/live.m3u8") → absolute URL.
export const liveMediaUrl = (relPath, cacheBust = null) => {
  const rel = `${relPath || ''}`.replace(/^\/+/, '')
  if (!rel) return ''
  const url = `${S3}/${rel}`
  return cacheBust ? `${url}?t=${cacheBust}` : url
}
